import {
  HEATS_PREFETCH_COUNT,
  HeatsTimeRangeContext,
  HeatsTimeRangeContextType,
} from "components/strategyDashboard/heats/context/HeatsTimeRangeContext";
import { useTenantData } from "contexts/tenant";
import dayjs, { Dayjs } from "dayjs";
import { useTenant } from "hooks/settings";
import { useDayjsDateStringParamState } from "hooks/useDayjsDateStringParamState";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { dateToMidnightRange } from "hooks/usePrecachedHeats";
import {
  createContext,
  ReactNode,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetStrategyDashboardHeatsQuery } from "src/store/api/generatedApi";

export interface DayNavigationContextType {
  date: Dayjs;
  setDate: (date: Dayjs) => void;
}

const DayNavigationContext = createContext<DayNavigationContextType | null>(
  null
);

export const useDayNavigation = (): DayNavigationContextType | null => {
  return use(DayNavigationContext);
};

interface DayNavigationProviderProps {
  children: ReactNode;
}

export const DayNavigationProvider = ({
  children,
}: DayNavigationProviderProps) => {
  const { localTimezone } = useLocalDatetime();
  const tenantName = useTenant();
  const tenantData = useTenantData();
  const heatDayStartHour = tenantData.heat_day_start_hour;

  const initialHasDateParam = useRef(
    new URLSearchParams(window.location.search).has("date")
  ).current;

  const [date, setDateRaw] = useDayjsDateStringParamState(
    "date",
    dayjs().tz(localTimezone),
    localTimezone
  );
  const [userNavigated, setUserNavigated] = useState(false);
  const useLatestMode = !initialHasDateParam && !userNavigated;

  const setDate = useCallback(
    (d: Dayjs) => {
      setDateRaw(d);
      setUserNavigated(true);
    },
    [setDateRaw]
  );

  const { data: latestData } = useGetStrategyDashboardHeatsQuery(
    { tenantName, start: "latest", filterMode: "fully_contained" },
    { skip: !useLatestMode }
  );

  useEffect(() => {
    if (!useLatestMode || !latestData?.resolved_day) return;
    const resolvedDate = dayjs(latestData.resolved_day).tz(localTimezone, true);
    if (resolvedDate.isValid()) {
      setDateRaw(resolvedDate);
    }
  }, [useLatestMode, latestData?.resolved_day, setDateRaw, localTimezone]);

  const timeRange = useMemo(
    () => dateToMidnightRange(date, localTimezone, heatDayStartHour),
    [date, localTimezone, heatDayStartHour]
  );

  const prefetchRanges = useMemo(
    () =>
      useLatestMode
        ? []
        : Array.from({ length: HEATS_PREFETCH_COUNT }, (_, i) =>
            dateToMidnightRange(
              date.subtract(i + 1, "day"),
              localTimezone,
              heatDayStartHour
            )
          ),
    [date, localTimezone, heatDayStartHour, useLatestMode]
  );

  const timeRangeValue: HeatsTimeRangeContextType = useMemo(
    () => ({
      viewMode: "day" as const,
      timeRange,
      day: useLatestMode ? "latest" : undefined,
      prefetchRanges,
      filterMode: "fully_contained" as const,
      isLoading: false,
      isError: false,
    }),
    [timeRange, useLatestMode, prefetchRanges]
  );

  const dayNavValue: DayNavigationContextType = useMemo(
    () => ({ date, setDate }),
    [date, setDate]
  );

  return (
    <HeatsTimeRangeContext value={timeRangeValue}>
      <DayNavigationContext value={dayNavValue}>
        {children}
      </DayNavigationContext>
    </HeatsTimeRangeContext>
  );
};
